import React, { ErrorInfo } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { normalizeError, NormalizedError } from '@/api/error/errorNormalizer';
import ErrorFallback from './ErrorFallback';

// Remove the inheritance from ErrorBoundaryProps to avoid type conflicts
interface ErrorBoundaryWrapperProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
  logError?: boolean;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
  onReset?: (...args: unknown[]) => void;
  resetKeys?: unknown[];
}

/**
 * Enhanced error boundary that uses our error normalizer
 * and provides default error handling capabilities
 */
interface ErrorBoundaryWrapperProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
  logError?: boolean;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
  onReset?: (...args: unknown[]) => void;
  resetKeys?: unknown[];
}

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  fallback = ErrorFallback,
  logError = true,
  onError,
  ...props
}) => {
  const handleError = (error: unknown, info: React.ErrorInfo) => {
    // Normalize the error
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const normalizedError = normalizeError(errorObj);

    // Add required Error properties to the normalized error
    const enhancedError = {
      ...normalizedError,
      name: 'NormalizedError',
      stack: errorObj.stack,
    } as Error & NormalizedError;

    // Log the error if requested
    if (logError) {
      console.error(
        `Error caught by boundary (ID: ${normalizedError.errorId}):`,
        normalizedError,
        info,
      );

      // Here you could add integration with error tracking services
      // TODO: Add error tracking service integration (sentry)

      //
      // Example:
      // if (window.Sentry) {
      //   window.Sentry.captureException(error, {
      //     extra: {
      //       componentStack: info.componentStack,
      //       errorId: normalizedError.errorId
      //     }
      //   });
      // }
    }

    if (onError) {
      onError(enhancedError, info);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={fallback}
      onError={handleError}
      onReset={props.onReset}
      resetKeys={props.resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
